import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  Button,
  IconButton,
  Modal,
  styled,
} from '@mui/material';

// Modal Container and Structure - Shared across all steps
export const ModalContainer = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

export const ModalContent = styled(Box)(({ theme }) => ({
  width: '90%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(3),
  position: 'relative',
  outline: 'none',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 12px 24px rgba(0, 0, 0, 0.15)',
  border: `1px solid ${theme.palette.divider}`,
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  fontSize: '17px',
  lineHeight: '22px',
  letterSpacing: '-0.408px',
  fontWeight: 600,
}));

// Navigation Buttons - Shared across all steps
export const CloseButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1,
}));

export const BackButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 1,
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.colors.blue,
  '&:hover': {
    backgroundColor: 'rgba(10, 132, 255, 0.1)',
    transform: 'translateY(-1px)',
  },
}));

// Button Container - Shared across steps that have buttons
export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

// Utility Containers - Shared across all steps
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

// Step Content Containers
export const StepContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

// Options Step Styles
export const OptionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const OptionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.translucentBlue,
  color: theme.colors.blue,
  borderRadius: theme.spacing(1.5),
  minHeight: '48px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.colors.blue,
    color: theme.colors.white,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.colors.blue}40`,
  },
  '&:active': {
    transform: 'translateY(0)',
    transition: 'all 0.1s ease',
  },
  '&:disabled': {
    backgroundColor: theme.colors.gray,
    color: theme.colors.white,
    opacity: 0.6,
    transform: 'none',
  },
}));

// Selection Step Styles
export const SelectionDescription = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  opacity: 0.7,
  marginBottom: theme.spacing(3),
  fontSize: '0.875rem',
}));

export const BreaksList = styled(List)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1),
}));

export const BreakListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(1),
}));

export const BreakListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.secondary,
  '&:hover': {
    backgroundColor: theme.palette.background.secondaryHover,
    transform: 'translateY(-1px)',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.background.selected,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.background.secondarySelected,
    },
  },
}));

export const BreakListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontWeight: 500,
    fontSize: '1rem',
  },
  '& .MuiListItemText-secondary': {
    opacity: 0.8,
    fontSize: '0.875rem',
  },
}));

// Confirmation Step Styles
export const ModalContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

export const CloseButtonWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
}));

export const BreakInfoText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  fontSize: '0.875rem',
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.colors.gray,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.white,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.blue,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.colors.gray,
    '&.Mui-focused': {
      color: theme.colors.blue,
    },
  },
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: '200px',
  backgroundColor: theme.palette.info.main,
  color: theme.colors.white,
  borderRadius: '12px',
  height: '40px',
  '&:hover': {
    backgroundColor: theme.colors.blueHover,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: theme.colors.gray,
    color: theme.colors.white,
  },
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  minWidth: '200px',
  backgroundColor: theme.palette.error.main,
  color: theme.colors.white,
  borderRadius: '12px',
  height: '40px',
  '&:hover': {
    backgroundColor: theme.colors.blueHover,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: theme.colors.gray,
    color: theme.colors.white,
  },
}));

// Warning Styles
export const WarningContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.warning.main}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const WarningText = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.dark,
  fontSize: '0.875rem',
  lineHeight: 1.4,
}));

export const BreakDetailsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));
